import React, { useEffect } from "react";
import clsx from "clsx";
import { Box, Typography } from "@material-ui/core";
import { DO_NOT_APPLY_REWARDS_KEY, useIsTravelApp, isVesperTenant, getConfigTenant } from "@capone/common";
import { shouldAutoSelectCard } from "@capone/common/src/utils/shouldAutoSelectCard";

import {
  CardPaymentEventTypes,
  CardPaymentSelectors,
  cartQuoteSelectors,
  getNestedChildState,
  PaymentInformationChildState,
  RewardsAndPayment,
  RewardsPaymentEventTypes,
  RewardsPaymentSelectors,
  useCheckoutState,
  useCheckoutStateSelector,
  WalletEventTypes,
  WalletSelectors,
} from "@capone/checkout";
import {
  ActionButton,
  B2BSpinner,
  BackButton,
  filterOutSpendAndGetOffers,
  GenericInfoPopup,
  Icon,
  IconName,
  LoadingPopup,
  MobileFloatingButton,
  MobilePopoverCard,
  PoweredByHopper,
  TEST_CARD_LAST_FOURS,
  useDeviceTypes,
  Tooltip,
  getHSPEarnOfferDescription,
} from "halifax";
import {
  TokenizeCardErrors,
  TravelProductEnum,
  TravelWalletCredit,
  TravelWalletOffer,
} from "redmond";

import { config } from "../../../../api/config";
import {
  AVAILABLE,
  C1_AUTO_SELECT_CARD_AT_CHECKOUT,
  getExperimentVariant,
  useExperiments,
} from "../../../../context/experiments";
import { ActivityMachineContext } from "../../state/types";
import { Event, TEvent } from "../../state/events";
import { getErrorModalProps } from "./utils";
import {
  CARD_VERIFY_TEXT,
  MOBILE_HEADING_SUBTITLE_TEXT,
  MOBILE_HEADING_TITLE_TEXT,
  NO_SELECTED_CARD_TEXT,
  NO_SELECTED_REWARDS_TEXT,
  PAYMENT_EARN_INFO_TOOLTIP_TEXT,
  REVIEW_TRIP_CTA_TEXT,
} from "./textConstants";
import "./styles.scss";
import { ClientContext } from "../../../../App";
import { CONFIRM_AND_BOOK_CTA_TEXT } from "../textConstants";
import { PriceBreakdown } from "../PriceBreakdown";
import { DesktopActivityBookValidationError } from "../DesktopActivityBookWorkflow";
import { productToEarnV2 } from "../../../../api/v1/rewards/productToEarn";
import dayjs from "dayjs";

export interface IActivityRewardsAndPaymentProps {
  isMobile?: boolean;
  validationErrorTypes?: DesktopActivityBookValidationError[];
  setValidationErrorTypes?: (
    types: DesktopActivityBookValidationError[],
  ) => void;
  isXmodRedesign?: boolean;
}

export const ActivityRewardsAndPayment = ({
  isMobile,
  validationErrorTypes,
  setValidationErrorTypes,
  isXmodRedesign,
}: IActivityRewardsAndPaymentProps) => {
  const { sessionInfo, isRewardsDisabled } = React.useContext(ClientContext);

  const { matchesMobile } = useDeviceTypes();

  const [tokenizeErrors, setTokenizeErrors] = React.useState<
    TokenizeCardErrors[]
  >([]);
  const [invalidCapOneCardError, setInvalidCapOneCardError] =
    React.useState(false);
  const [state, send] = useCheckoutState<TEvent, ActivityMachineContext>();

  const { isTravelApp } = useIsTravelApp();
  const isVesper = isVesperTenant(getConfigTenant());

  const nestedChildState = getNestedChildState(state.value);

  /* selectors */

  const paymentMethods = useCheckoutStateSelector(
    CardPaymentSelectors.getPaymentMethods,
  );

  const offers = useCheckoutStateSelector(WalletSelectors.getOffers);
  const credits = useCheckoutStateSelector(
    WalletSelectors.getApplicableCredits,
  );
  const ineligibleCreditsBreakdown = useCheckoutStateSelector(
    WalletSelectors.getIneligibleCreditsBreakdown,
  );

  const offerToApply = useCheckoutStateSelector(
    WalletSelectors.getSelectedOffer,
  );
  const creditsToApply = useCheckoutStateSelector(
    WalletSelectors.getTravelWalletCreditToApply,
  );
  const isTravelCreditPaymentOnly = useCheckoutStateSelector(
    WalletSelectors.getIsTravelCreditPaymentOnly,
  );
  const isTravelWalletOfferPaymentOnly = useCheckoutStateSelector(
    WalletSelectors.getIsTravelWalletOfferPaymentOnly,
  );
  const isStackedTravelWalletPaymentOnly = useCheckoutStateSelector(
    WalletSelectors.getIsStackedTravelWalletPaymentOnly,
  );
  const creditTransactionHistory = useCheckoutStateSelector(
    WalletSelectors.getCreditTransactionHistory,
  );
  const maxApplicableTravelWalletCredit = useCheckoutStateSelector(
    WalletSelectors.getMaxApplicableCreditAmount,
  );

  const paymentMethodsLoading = useCheckoutStateSelector(
    CardPaymentSelectors.getIsPaymentLoading,
  );
  const selectedPaymentMethodId = useCheckoutStateSelector(
    CardPaymentSelectors.getSelectedPaymentMethodId,
  );
  const isPaymentError = useCheckoutStateSelector(
    CardPaymentSelectors.getIsPaymentError,
  );
  const addPaymentMethodModalOpen = useCheckoutStateSelector(
    CardPaymentSelectors.getOpenAddPaymentMethodModal,
  );
  const cardPaymentAmountRequired = useCheckoutStateSelector(
    CardPaymentSelectors.getTotalCardPaymentRequired,
  );

  const rewardsAccounts =
    useCheckoutStateSelector(RewardsPaymentSelectors.getRewardsAccounts) || [];
  const selectedRewardsAccountId = useCheckoutStateSelector(
    RewardsPaymentSelectors.getSelectedAccountId,
  );
  const cartBreakdownBalance = useCheckoutStateSelector(
    cartQuoteSelectors.getCartBreakdownBalance,
  );
  const rewardsPaymentInFiatCurrency = useCheckoutStateSelector(
    RewardsPaymentSelectors.getRewardsFiatAmountToApply,
  );
  const rewardsPaymentInRewardsCurrency = useCheckoutStateSelector(
    RewardsPaymentSelectors.getRewardsAmountToApply,
  );
  const isCreditCardPaymentRequired = useCheckoutStateSelector(
    CardPaymentSelectors.getIsCreditCardPaymentRequired,
  );

  const isCostCoveredBySelectedPaymentMethods = useCheckoutStateSelector(
    CardPaymentSelectors.getIsCostCoveredBySelectedPaymentMethods,
  );
  const earnValueByRewardsAccountId = useCheckoutStateSelector(
    RewardsPaymentSelectors.getEarnValueByRewardsAccount,
  );
  const rewardsPaymentAllowed = useCheckoutStateSelector(
    RewardsPaymentSelectors.getRewardsPaymentAllowed,
  );

  /* action callbacks */

  const verifyPaymentMethod = (token: string) =>
    send({ type: Event.VERIFY_PAYMENT_METHOD, token });

  const deletePaymentMethod = (paymentId: string) =>
    send({ type: Event.DELETE_PAYMENT_METHOD, paymentMethod: { paymentId } });

  const setSelectedPaymentMethodId = (
    paymentMethodId: string,
    rewardsAccountId?: string,
  ) => {
    send({
      type: Event.SET_SELECTED_PAYMENT_METHOD_ID,
      paymentMethod: { paymentId: paymentMethodId, rewardsAccountId },
    });
  };

  const handlePaymentMethodAdd = (token: string, last4: string) => {
    const account = rewardsAccounts.find(
      (act) =>
        act.lastFour === last4 ||
        act.lastFourVirtualCardNumbers?.includes(last4),
    );
    let matchingRewardsAccount = account;

    const isTestCard =
      window.__mclean_env__.ENV !== "production" &&
      TEST_CARD_LAST_FOURS.includes(last4);
    if (isTestCard) {
      // eslint-disable-next-line prefer-destructuring
      matchingRewardsAccount = rewardsAccounts[0];
    }

    if (!!matchingRewardsAccount || isTestCard) {
      verifyPaymentMethod(token);
      setSelectedPaymentMethodId(
        token,
        matchingRewardsAccount?.accountReferenceId,
      );
    } else {
      send(Event.CLOSE_PAYMENT_FORM);
      setInvalidCapOneCardError(true);
    }
  };

  const handleClearErrors = () => {
    send(Event.CLEAR_PAYMENT_ERROR);
    setTokenizeErrors([]);
    setInvalidCapOneCardError(false);
  };

  const handleClickAddPaymentMethod = () => send(Event.OPEN_PAYMENT_FORM);

  const handleCloseAddPaymentMethod = () => send(Event.CLOSE_PAYMENT_FORM);

  const setSelectedRewardsAccountId = (accountId?: string) =>
    send({
      type: RewardsPaymentEventTypes.SET_SELECTED_REWARDS_ACCOUNT_ID,
      selectedAccountId: accountId,
    });

  const setRewardsPaymentAmount = (amount: number) =>
    send({
      type: RewardsPaymentEventTypes.SET_REWARDS_AMOUNT_TO_PAY,
      rewardsAmountToPay: amount,
    });

  const setOfferToApply = (offer?: TravelWalletOffer) =>
    offer
      ? send({ type: WalletEventTypes.SET_SELECTED_OFFER, offerId: offer.id })
      : send(WalletEventTypes.REMOVE_SELECTED_OFFER);

  const setCreditAmountToApply = (walletCredits?: TravelWalletCredit) => {
    setSelectedRewardsAccountId();
    setRewardsPaymentAmount(0);
    send({
      type: WalletEventTypes.SET_CREDIT_AMOUNT_TO_APPLY,
      amount: walletCredits?.amount?.amount || 0,
    });
  };

  const canContinue =
    isMobile ||
    (isCostCoveredBySelectedPaymentMethods && !!selectedRewardsAccountId);

  const handleContinue = () => {
    send({
      type: Event.SET_INITIAL_BOOK_TIME,
      initialTime: dayjs().toISOString(),
    });
    if (canContinue) {
      setValidationErrorTypes?.([]);
      send(Event.NEXT);
    } else {
      setValidationErrorTypes?.(
        (() => {
          switch (true) {
            case !selectedRewardsAccountId:
              return ["rewards"];
            case isCreditCardPaymentRequired && !selectedPaymentMethodId:
              return ["card"];
            default:
              return [];
          }
        })(),
      );
    }
  };

  const handleBack = () => {
    send(Event.PREVIOUS);
  };

  const errorModalOpen =
    !!tokenizeErrors.length || invalidCapOneCardError || isPaymentError;

  const errorModalProps = getErrorModalProps(
    tokenizeErrors,
    invalidCapOneCardError,
    handleClearErrors,
  );

  useEffect(() => {
    if (
      (validationErrorTypes?.includes("rewards") && selectedRewardsAccountId) ||
      (validationErrorTypes?.includes("card") &&
        isCreditCardPaymentRequired &&
        selectedPaymentMethodId)
    ) {
      setValidationErrorTypes?.([]);
    }
  }, [
    selectedRewardsAccountId,
    isCreditCardPaymentRequired,
    selectedPaymentMethodId,
    validationErrorTypes,
  ]);

  useEffect(() => {
    if (isCostCoveredBySelectedPaymentMethods && !selectedRewardsAccountId) {
      setSelectedRewardsAccountId(DO_NOT_APPLY_REWARDS_KEY);
    }
  }, [isCostCoveredBySelectedPaymentMethods, selectedRewardsAccountId]);

  const filteredOffers = React.useMemo(
    () => (offers ? filterOutSpendAndGetOffers(offers) : undefined),
    [offers],
  );

  const expState = useExperiments();
  const autoSelectExperimentOn =
    getExperimentVariant(
      expState.experiments,
      C1_AUTO_SELECT_CARD_AT_CHECKOUT,
    ) === AVAILABLE;

  React.useEffect(() => {
    // Wallet machine always completes before RewardsAndPayment renders
    // (xstate sequences wallet -> cardPayment), so walletLoaded is always true here.
    const toSelect = shouldAutoSelectCard({
      paymentMethods,
      filteredOffers,
      credits,
      selectedPaymentMethodId,
      experimentOn: autoSelectExperimentOn,
      walletLoaded: true,
    });
    if (!toSelect) return;
    const card = paymentMethods[0];
    const matchingRewardsAccount = rewardsAccounts.find(
      (account) =>
        account.lastFour === card.last4 ||
        (card.last4 &&
          account.lastFourVirtualCardNumbers?.includes(card.last4)),
    );
    send({
      type: CardPaymentEventTypes.SET_SELECTED_PAYMENT_METHOD_ID,
      paymentMethod: {
        paymentId: toSelect,
        rewardsAccountId: matchingRewardsAccount?.accountReferenceId,
      },
    });
  }, [
    paymentMethods,
    filteredOffers,
    credits,
    autoSelectExperimentOn,
    rewardsAccounts,
    send,
  ]);

  useEffect(() => {
    if (cardPaymentAmountRequired !== undefined) {
      Promise.all(
        rewardsAccounts.map((account) =>
          productToEarnV2({
            account: account.accountReferenceId,
            earnItems: {
              // TODO update to activity name
              [TravelProductEnum.Experiences]: {
                amountUsd: cardPaymentAmountRequired,
                earnOfferDescription: getHSPEarnOfferDescription(offerToApply),
              },
            },
          })
            .then((res) => {
              send({
                type: RewardsPaymentEventTypes.SET_EARN_BY_REWARDS_ACCOUNT_ID,
                accountReferenceId: account.accountReferenceId,
                earnResponse: res,
              });
            })
            .catch((e) =>
              console.warn(
                `Failed to fetch earn for account ${account.accountReferenceId}: ${e}`,
              ),
            ),
        ),
      );
    }
  }, [cardPaymentAmountRequired, offerToApply]);

  const renderContent = () => (
    <RewardsAndPayment
      rewardsAccounts={rewardsAccounts}
      paymentMethods={paymentMethods}
      loadingPaymentMethods={paymentMethodsLoading}
      spreedlyEnvironmentKey={config.spreedlyEnvironmentKey}
      selectedPaymentMethodId={selectedPaymentMethodId}
      verifyingPaymentMethod={
        nestedChildState === PaymentInformationChildState.verify
      }
      isMobile={matchesMobile}
      onRemovePaymentMethod={deletePaymentMethod}
      onSelectPaymentMethod={setSelectedPaymentMethodId}
      onAddPaymentMethod={handlePaymentMethodAdd}
      onAddPaymentMethodError={setTokenizeErrors}
      onClickAddPaymentMethod={handleClickAddPaymentMethod}
      closePaymentModal={handleCloseAddPaymentMethod}
      addPaymentModalOpen={addPaymentMethodModalOpen}
      selectedRewardsAccountId={selectedRewardsAccountId}
      setSelectedRewardsAccountId={setSelectedRewardsAccountId}
      total={
        cartBreakdownBalance
          ? {
              ...cartBreakdownBalance,
              rewards: cartBreakdownBalance.accountSpecific,
            }
          : undefined
      }
      rewardsPaymentInFiatCurrency={rewardsPaymentInFiatCurrency}
      rewardsPaymentInRewardsCurrency={rewardsPaymentInRewardsCurrency}
      setRewardsPaymentAmount={setRewardsPaymentAmount}
      isCreditCardPaymentRequired={isCreditCardPaymentRequired}
      offers={offers}
      credits={credits}
      offerToApply={offerToApply}
      creditsToApply={creditsToApply}
      isTravelCreditPaymentOnly={isTravelCreditPaymentOnly}
      isTravelWalletOfferPaymentOnly={isTravelWalletOfferPaymentOnly}
      isStackedTravelWalletPaymentOnly={isStackedTravelWalletPaymentOnly}
      firstName={sessionInfo?.userInfo.firstName || ""}
      creditTransactionHistory={creditTransactionHistory}
      setOfferToApply={setOfferToApply}
      setCreditToApply={setCreditAmountToApply}
      maxApplicableTravelWalletCredit={maxApplicableTravelWalletCredit}
      cardPaymentDisabled={
        selectedRewardsAccountId ? !isCreditCardPaymentRequired : true
      }
      earnValueByRewardsAccountId={earnValueByRewardsAccountId}
      rewardsRedemptionIneligible={!rewardsPaymentAllowed}
      rewardsError={
        validationErrorTypes?.includes("rewards")
          ? NO_SELECTED_REWARDS_TEXT
          : undefined
      }
      cardError={
        validationErrorTypes?.includes("card")
          ? NO_SELECTED_CARD_TEXT
          : undefined
      }
      product={TravelProductEnum.Experiences}
      ineligibleCreditsBreakdown={ineligibleCreditsBreakdown}
      isHAModeRewardsDisabled={isRewardsDisabled}
    />
  );

  return (
    <>
      {isMobile ? (
        <MobilePopoverCard
          open
          className={clsx("activity-book-mobile-popover", {
            xmod: isXmodRedesign,
            redesigned: isVesper,
          })}
          fullScreen
          onClose={() => {}}
          topLeftButton={
            !isTravelApp ? (
              <BackButton
                className="activity-book-mobile-popover-back-button"
                onClick={handleBack}
              />
            ) : undefined
          }
          headerElement={<PriceBreakdown isMobile dropdown />}
          showTransition={false}
        >
          <Box className="activity-book-mobile-header">
            <Typography className="mobile-header-title" variant="h3">
              {MOBILE_HEADING_TITLE_TEXT}
            </Typography>
            <Box className="mobile-header-subtitle-wrapper">
              <Typography
                className={clsx("mobile-header-subtitle", {
                  vesper: isVesper,
                })}
                variant="body2"
              >
                {MOBILE_HEADING_SUBTITLE_TEXT}
              </Typography>

              <Tooltip
                content={
                  <Typography className="payment-earn-info-popover-content">
                    {PAYMENT_EARN_INFO_TOOLTIP_TEXT}
                  </Typography>
                }
                triggerClassName="payment-earn-info-btn"
                title="Rewards & Payment"
              >
                <Icon name={IconName.InfoCircle} />
              </Tooltip>
            </Box>
          </Box>
          <hr className="heading-rule" />
          {renderContent()}
          {isCostCoveredBySelectedPaymentMethods && (
            <MobileFloatingButton
              className="activity-book-mobile-continue-button"
              onClick={handleContinue}
              wrapperClassName="b2b"
            >
              {REVIEW_TRIP_CTA_TEXT}
            </MobileFloatingButton>
          )}
        </MobilePopoverCard>
      ) : (
        <>
          {renderContent()}
          <ActionButton
            onClick={handleContinue}
            message={CONFIRM_AND_BOOK_CTA_TEXT}
            className="activity-continue-button book"
            disabled={!isCostCoveredBySelectedPaymentMethods}
          />
        </>
      )}
      <GenericInfoPopup
        open={errorModalOpen}
        image={<Icon className="error-icon" name={IconName.ErrorState} />}
        zIndex={1301}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...errorModalProps}
      />
      <LoadingPopup
        classes={[
          clsx("activity-book-validating-card-modal", {
            mobile: matchesMobile,
          }),
        ]}
        indicatorSize="small"
        indicator={B2BSpinner}
        open={nestedChildState === PaymentInformationChildState.verify}
        popupSize="small"
        message={CARD_VERIFY_TEXT}
        footer={PoweredByHopper}
        textAlign="center"
        fullScreen={isMobile}
        verticalAlignment={isMobile ? "center" : undefined}
      />
    </>
  );
};
